/**
 * PastLivesCarousel — Embla-powered horizontal walk through past-life cards.
 *
 * Sort: cards delivered in deliveryIndex order (hero at 0, then layer-2 grids,
 * then layer-3). Dot strip below, cross-sell CTA after the last card.
 *
 * Share is a no-op stub that defers to Telegram WebApp.shareUrl when available;
 * downstream wave (017+ purchase polish) will wire proper sharing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useTranslation } from 'react-i18next';
import WebApp from '@twa-dev/sdk';
import type { PastLivesCard } from '../../hooks/usePastLivesRun';
import { PastLivesCardView } from './PastLivesCardView';
import styles from './PastLivesCarousel.module.css';

export interface PastLivesCarouselProps {
  cards: PastLivesCard[];
  onCrossSell?: () => void;
  onClose?: () => void;
  testId?: string;
}

export function PastLivesCarousel({
  cards,
  onCrossSell,
  onClose,
  testId = 'past-lives-carousel',
}: PastLivesCarouselProps): JSX.Element {
  const { t } = useTranslation('flows');
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'center' });
  const [selectedIdx, setSelectedIdx] = useState(0);

  const ordered = useMemo(
    () => [...cards].sort((a, b) => a.deliveryIndex - b.deliveryIndex),
    [cards],
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = (): void => setSelectedIdx(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  const scrollTo = useCallback((idx: number) => emblaApi?.scrollTo(idx), [emblaApi]);
  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const handleShare = useCallback((card: PastLivesCard): void => {
    try {
      const text = `${card.profession ?? card.era} — ${card.region}`;
      WebApp?.HapticFeedback?.impactOccurred?.('light');
      WebApp?.openLink?.(card.portraitUrl);
      WebApp?.showAlert?.(text);
    } catch {
      /* noop */
    }
  }, []);

  if (ordered.length === 0) {
    return <div className={styles.empty} data-testid={`${testId}-empty`}>—</div>;
  }

  return (
    <div className={styles.root} data-testid={testId}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          {t('pastLives.carousel.title', { defaultValue: 'Your past lives' })}
        </div>
        {onClose ? (
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            ×
          </button>
        ) : null}
      </header>

      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.container}>
          {ordered.map((card, i) => (
            <div className={styles.slide} key={card.id}>
              <PastLivesCardView
                card={card}
                position={i + 1}
                total={ordered.length}
                onShare={handleShare}
              />
            </div>
          ))}
        </div>
      </div>

      <div className={styles.controls}>
        <button
          type="button"
          className={styles.arrowBtn}
          onClick={scrollPrev}
          disabled={selectedIdx === 0}
          aria-label={t('pastLives.carousel.prev', { defaultValue: 'Previous life' })}
        >
          ‹
        </button>

        <div className={styles.dots} role="tablist" aria-label={t('pastLives.carousel.dots', { defaultValue: 'Lives' })}>
          {ordered.map((card, i) => (
            <button
              key={card.id}
              type="button"
              className={`${styles.dot} ${selectedIdx === i ? styles.dotActive : ''}`}
              aria-selected={selectedIdx === i}
              onClick={() => scrollTo(i)}
            />
          ))}
        </div>

        <button
          type="button"
          className={styles.arrowBtn}
          onClick={scrollNext}
          disabled={selectedIdx === ordered.length - 1}
          aria-label={t('pastLives.carousel.next', { defaultValue: 'Next life' })}
        >
          ›
        </button>
      </div>

      {onCrossSell && selectedIdx === ordered.length - 1 ? (
        <div className={styles.crossSell}>
          <p className={styles.crossSellHint}>
            {t('pastLives.carousel.crossSellHint', {
              defaultValue: 'Wanting more? Step into a stylized avatar of the present life.',
            })}
          </p>
          <button
            type="button"
            className={styles.crossSellBtn}
            onClick={onCrossSell}
            data-testid="past-lives-cross-sell"
          >
            {t('pastLives.carousel.crossSellCta', { defaultValue: 'Explore Avatar Packs →' })}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default PastLivesCarousel;
